import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Alert } from '@mui/material';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import axiosInstance from '../../services/axiosInstance';
import { ctGetRecentActivity, ctListBatches } from '../../services/api';
import InstitutionPageHeader from '../../components/InstitutionPageHeader';
import { useBranding } from '../../context/BrandingContext';
import DashboardMetricCard from '../../components/Cards/DashboardMetricCard';

const AdminOverview = () => {
  const { displayShortName } = useBranding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [batches, setBatches] = useState(null);
  const [users, setUsers] = useState(null);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [usersRes, batchesRes, activityRes] = await Promise.all([
          axiosInstance.get('/admin/users', { params: { page: 1, limit: 1 } }).catch(() => null),
          ctListBatches().catch(() => null),
          ctGetRecentActivity().catch(() => null),
        ]);
        if (cancelled) return;
        setUsers(usersRes?.data?.pagination?.total ?? null);
        const batchList = batchesRes?.data?.batches;
        setBatches(Array.isArray(batchList) ? batchList.length : null);
        const events = activityRes?.data?.events;
        setActivity(Array.isArray(events) ? events.length : null);
      } catch {
        if (!cancelled) setError('Some metrics could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <InstitutionPageHeader />
      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5, letterSpacing: '-0.02em' }}>
        {displayShortName} · Platform overview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 480 }}>
        System snapshot: batches, people, and recent signals.
      </Typography>

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <DashboardMetricCard
            to="/admin/coding-tracker"
            icon={<HubOutlinedIcon fontSize="small" />}
            label="Platform overview"
            value={batches ?? '—'}
            loading={loading}
            hint="Batches"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DashboardMetricCard
            to="/admin/users"
            icon={<PeopleOutlineOutlinedIcon fontSize="small" />}
            label="Total users"
            value={users ?? '—'}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DashboardMetricCard
            to="/admin/plagiarism-analytics"
            icon={<InsightsOutlinedIcon fontSize="small" />}
            label="Activity summary"
            value={activity ?? '—'}
            loading={loading}
            hint="Recent events"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminOverview;
